import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Recommendation, StudentStore } from '@coach-bot/data-access/shared';
import {
  faArrowsRotate,
  faCalculator,
  faCheckCircle,
  faCircleQuestion,
  faFaceSmileWink,
} from '@fortawesome/free-solid-svg-icons';
import { ExtensionCommunicationService } from '@shared/extension';
import { AbstractComponent } from '@shared/ui';
import { BehaviorSubject, Observable, combineLatest, map, takeUntil } from 'rxjs';

enum View {
  Loading = 'Loading',
  LoadingFailed = 'LoadingFailed',
  NoRecommendations = 'NoRecommendations',
  Recommendations = 'Recommendations',
}

@Component({
  selector: 'st-recommendations',
  templateUrl: './recommendations.component.html',
  styleUrls: ['./recommendations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationsComponent extends AbstractComponent implements OnInit {
  @Output() changeSubject = new EventEmitter<string>();

  readonly currentUrl$: BehaviorSubject<string> = this.studentStore.currentUrl$;
  readonly recommendations$: BehaviorSubject<Recommendation[] | null> = this.studentStore.recommendations$;
  readonly recommendationsLoadingFailed$: BehaviorSubject<boolean> = this.studentStore.recommendationsLoadingFailed$;
  readonly waitingForRewardRecommendation$: BehaviorSubject<Recommendation | null> =
    this.studentStore.waitingForRewardRecommendation$;

  allRecommendations$: Observable<Recommendation[] | null>;
  readonly view$ = new BehaviorSubject<View>(View.Loading);

  readonly View = View;

  readonly icons = {
    faArrowsRotate,
    faCalculator,
    faCheckCircle,
    faCircleQuestion,
    faFaceSmileWink,
  };

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private extensionCommunicationService: ExtensionCommunicationService,
    private studentStore: StudentStore
  ) {
    super();
  }

  ngOnInit(): void {
    this.allRecommendations$ = combineLatest([this.recommendations$, this.waitingForRewardRecommendation$]).pipe(
      takeUntil(this.destroyed$),
      map(([recommendations, waitingForRewardRecommendation]) => {
        if (!recommendations && !waitingForRewardRecommendation) {
          return null;
        }
        let allRecommendations: Recommendation[] = recommendations || [];
        if (
          !!waitingForRewardRecommendation &&
          !allRecommendations.some(item => item.url === waitingForRewardRecommendation.url)
        ) {
          allRecommendations = [waitingForRewardRecommendation, ...allRecommendations];
        }
        return allRecommendations;
      })
    );

    combineLatest([this.allRecommendations$, this.recommendationsLoadingFailed$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([allRecommendations, recommendationsLoadingFailed]: [Recommendation[] | null, boolean]) => {
        let view: View;
        if (recommendationsLoadingFailed) {
          view = View.LoadingFailed;
        } else if (allRecommendations) {
          if (allRecommendations.length) {
            view = View.Recommendations;
          } else {
            view = View.NoRecommendations;
          }
        } else {
          view = View.Loading;
        }
        if (view !== this.view$.value) {
          this.view$.next(view);
        }
      });

    combineLatest([this.allRecommendations$, this.currentUrl$, this.recommendationsLoadingFailed$, this.view$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  onChangeSubject(): void {
    this.changeSubject.emit();
  }

  reloadRecommendations(): void {
    this.extensionCommunicationService.loadRecommendations();
  }

  isWaitingForReward$(recommendation: Recommendation): Observable<boolean> {
    return this.waitingForRewardRecommendation$.pipe(
      map(waitingForRewardRecommendation => {
        return !!waitingForRewardRecommendation && waitingForRewardRecommendation.url === recommendation.url;
      })
    );
  }
}
